import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";

const RANGES = [["7d", "7 days"], ["30d", "30 days"], ["all", "All time"]];

export default function OfferEnquiriesSection() {
  const { hasPerm } = useAuth();
  const allowed = hasPerm("view_reports");
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!allowed) return;
    api.get(`/reports/offer-enquiries?range=${range}`).then(({ data }) => setData(data))
      .catch((e) => toast.error(formatError(e)));
  }, [range, allowed]);

  if (!allowed || !data) return null;

  const exportCsv = async () => {
    try {
      const res = await api.get(`/reports/offer-enquiries.csv?range=${range}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `zola-offer-enquiries-${range}.csv`;
      a.click();
    } catch (e) { toast.error(formatError(e)); }
  };

  const max = Math.max(1, ...data.top_offers.map((o) => o.total));
  return (
    <div className="w-card w-card-bordered p-5" data-testid="offer-enquiries-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="label-caps gold">Offer enquiries (WhatsApp)</div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full p-0.5" style={{ background: "var(--bg-soft)" }}>
            {RANGES.map(([k, l]) => (
              <button key={k} type="button" onClick={() => setRange(k)} data-testid={`offer-enq-range-${k}`}
                      className={`px-3 h-7 rounded-full text-xs font-medium transition-colors ${range === k ? "bg-navy text-white" : ""}`}>
                {l}
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" className="rounded-full h-8" onClick={exportCsv}
                  data-testid="offer-enq-csv-btn"><Download className="h-3.5 w-3.5 mr-1" /> CSV</Button>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-4">
        <div><div className="text-xs" style={{ color: "var(--text-muted)" }}>Total</div>
          <div className="num-big" data-testid="offer-enq-total">{data.total}</div></div>
        <div><div className="text-xs" style={{ color: "var(--text-muted)" }}>Landing Page</div>
          <div className="num-big" data-testid="offer-enq-landing">{data.by_source.landing_page}</div></div>
        <div><div className="text-xs" style={{ color: "var(--text-muted)" }}>Customer Portal</div>
          <div className="num-big gold" data-testid="offer-enq-portal">{data.by_source.customer_portal}</div></div>
      </div>
      <ul className="mt-5 space-y-3 text-sm" data-testid="offer-enq-top">
        {data.top_offers.length === 0 && <li style={{ color: "var(--text-muted)" }}>No enquiries in this period.</li>}
        {data.top_offers.slice(0, 10).map((o) => (
          <li key={o.offer_id} data-testid={`offer-enq-row-${o.offer_id}`}>
            <div className="flex justify-between gap-3">
              <span className="font-medium truncate">{o.title}</span>
              <span className="font-semibold whitespace-nowrap">{o.total}
                <span className="ml-2 text-xs font-normal" style={{ color: "var(--text-muted)" }}>
                  LP {o.landing_page} · CP {o.customer_portal}
                </span>
              </span>
            </div>
            <div className="mt-1 h-1.5 rounded-full" style={{ background: "var(--bg-soft)" }}>
              <div className="h-full rounded-full" style={{ width: `${(o.total / max) * 100}%`, background: "#25D366" }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
